import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppService } from './app.service';
import { LocalService } from './local.service';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  token: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export enum UserRole {
  SystemAdmin = 'system_admin',
  None = 'none',
  Admin = 'admin',
  Librarian = 'librarian',
  Auditor = 'auditor',
  User = 'user',
  Publisher = 'publisher',
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private appService = inject(AppService);
  private localService = inject(LocalService);
  private router = inject(Router);

  private readonly userSignal = signal(this.localService.getData('edms_current_user'));

  // Computed signals
  readonly isAuthenticated = computed(() => this.userSignal() !== null);
  readonly user = computed<any>(() => this.userSignal());

  /** Current user role, falls back to None when unauthenticated */
  readonly role = computed<UserRole>(() => this.resolveRole(this.userSignal()));

  /** system_admin & admin — full access including user/role management */
  readonly canManageUsers = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin].includes(this.role()),
  );

  /** system_admin, admin & librarian — department / cabinet / folder management */
  readonly canManageLibrary = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin, UserRole.Librarian].includes(this.role()),
  );

  /** system_admin & admin — rename / delete / move documents */
  readonly canManageDocuments = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin].includes(this.role()),
  );

  /** system_admin, admin & publisher — upload documents */
  readonly canUpload = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin, UserRole.Publisher].includes(this.role()),
  );

  /** system_admin, admin & publisher — use the scanner */
  readonly canScan = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin, UserRole.Publisher].includes(this.role()),
  );

  /** system_admin, admin & publisher — initiate OCR / text extraction */
  readonly canOCR = computed(() =>
    [UserRole.SystemAdmin, UserRole.Admin, UserRole.Publisher].includes(this.role()),
  );

  constructor() {
    // Ensure auth state is restored immediately on service construction.
    this.refreshUser();
  }

  clearUserData() {
    this.localService.removeData('edms_current_user');
    this.userSignal.set(null);
  }

  login(data: LoginRequest) {
    return lastValueFrom(this.appService.post(`/Auth/Login`, data)).then((response) => {
      // Store user data in signal and localStorage
      this.userSignal.set(response);
      this.localService.saveData('edms_current_user', response);
      return response;
    });
  }

  resetPasswordRequest(data: ForgotPasswordRequest) {
    return lastValueFrom(this.appService.post(`/Auth/SendPasswordResetEmail`, data));
  }

  resetPassword(data: ResetPasswordRequest) {
    return lastValueFrom(this.appService.put(`/Auth/ResetPassword`, data));
  }

  verifyEmail(token: string) {
    return lastValueFrom(this.appService.get(`/Auth/VerifyEmail`, { token }));
  }

  resendEmailVerificationToken(email: string) {
    return lastValueFrom(this.appService.post(`/Auth/ResendEmailVerification`, { email }));
  }

  register(data: SignupRequest): Promise<any> {
    return lastValueFrom(this.appService.post(`/Auth/Register`, data));
  }

  changePassword(data: ChangePasswordRequest) {
    return lastValueFrom(this.appService.put(`/Auth/ChangePassword`, data));
  }

  updateProfile(data: UpdateProfileRequest) {
    return lastValueFrom(this.appService.put(`/Auth/UpdateProfile`, data));
  }

  /**
   * Logout user and clear session
   */
  logout(): void {
    this.userSignal.set(null);
    this.localService.removeData('edms_current_user');
    this.router.navigate(['/auth/login']);
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    const user = this.user() as any;
    return user?.authToken ?? user?.token ?? user?.accessToken ?? null;
  }

  /**
   * Check if user is authenticated
   */
  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Refresh user info from token (for initialization)
   */
  refreshUser(): void {
    const user = this.localService.getData('edms_current_user');

    if (user) {
      this.userSignal.set(user);
      return;
    }

    this.userSignal.set(null);
  }

  private resolveRole(user: any): UserRole {
    const rawRole =
      user?.role ??
      user?.roleName ??
      user?.RoleName ??
      user?.userProfile?.role ??
      user?.userProfile?.roleName ??
      user?.userProfile?.RoleName ??
      user?.userProfile?.user?.role ??
      user?.userProfile?.user?.roleName ??
      user?.userProfile?.user?.RoleName;

    return this.normalizeRole(rawRole);
  }

  private normalizeRole(rawRole: unknown): UserRole {
    if (typeof rawRole !== 'string') {
      return UserRole.None;
    }

    const normalized = rawRole
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

    switch (normalized) {
      case UserRole.SystemAdmin:
      case 'sysadmin':
      case 'super_admin':
      case 'superadmin':
        return UserRole.SystemAdmin;
      case UserRole.Admin:
        return UserRole.Admin;
      case UserRole.Librarian:
        return UserRole.Librarian;
      case UserRole.Auditor:
        return UserRole.Auditor;
      case UserRole.User:
        return UserRole.User;
      case UserRole.Publisher:
        return UserRole.Publisher;
      case UserRole.None:
      default:
        return UserRole.None;
    }
  }
}
